import { FC } from 'react';

import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Button } from '../../../custom';
import Input from '../../../custom/input/input';
import Select from '../../../custom/select/select';
import {
  StatusOptions,
  createOrUpdateApplicationFee,
  getAllApplicationBatch,
} from '../../../requests';
import { validator } from '../../../utils/validator';
import { getAllModeOfStudy, getProgramTypes } from '../applicationFeeSetup/request';
import { getAllProgram } from '../program/request';

interface ComponentProps {
  record: GetAllFeeSetup;
  handleClose: () => void;
}

const AddAcceptanceFee: FC<ComponentProps> = ({ record, handleClose }) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const toOptionalNumber = (value: unknown) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  };

  const createUpdateApplicationFeeMutation = useMutation({
    mutationFn: createOrUpdateApplicationFee,
    mutationKey: ['create-update-acceptanceFee'],
  });

  const createUpdateApplicationFeeHandler = async (values: FormikValues, resetForm: () => void) => {
    const baseAcceptanceFee = toOptionalNumber(values.acceptanceFee) ?? 0;
    const internationalAcceptanceFee = toOptionalNumber(values.internationalAcceptanceFee) ?? baseAcceptanceFee;
    const indigeneAcceptanceFee = toOptionalNumber(values.indigeneAcceptanceFee) ?? baseAcceptanceFee;

    const payload: Partial<ApplicationFee> & { acceptanceFee?: number } = {
      id: record?.id || 0,
      modeOfStudyId: Number(values.modeOfStudy),
      programId: values.programName,
      programTypeId: values.programType,
      activeStatus: values.status === 'true',
      applicationBatchId: Number(values.applicationBatch),
      acceptanceFee: baseAcceptanceFee,
      internationalAcceptanceFee,
      indigeneAcceptanceFee,
    };

    try {
      await createUpdateApplicationFeeMutation.mutateAsync(payload, {
        onSuccess: () => {
          notification.success({
            message: 'Success',
          });

          queryClient.refetchQueries({
            queryKey: ['get-all-fee-setup'],
          });

          resetForm();
          handleClose();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response.data?.message || error?.response?.data?.title,
      });
    }
  };

  const queries = useQueries({
    queries: [
      {
        queryKey: ['get-all-program'],
        queryFn: () => getAllProgram(),
      },
      { queryKey: ['get-ApplicationBatch'], queryFn: getAllApplicationBatch },
      { queryKey: ['get-program-types'], queryFn: getProgramTypes },
      { queryKey: ['mode-of-study'], queryFn: getAllModeOfStudy },
    ],
  });

  const programQuery = queries[0];
  const applicationBatchQuery = queries[1];
  const programTypesQuery = queries[2];
  const modeOfStudyQuery = queries[3];

  const programData = programQuery?.data?.data ?? [];
  const applicationBatchData = applicationBatchQuery?.data?.data ?? [];
  const programTypesData = programTypesQuery?.data?.data ?? [];
  const modeOfStudyData = modeOfStudyQuery?.data?.data ?? [];

  const programOptions = programData?.map((item) => (
    <option key={item?.id} value={item?.programId}>
      {item?.programName} {item?.programCode ? `(${item.programCode})` : ''}
    </option>
  ));

  const programTypesOptions = programTypesData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const modeOfStudyOptions = modeOfStudyData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.name}
    </option>
  ));

  const applicationBatchOptions = applicationBatchData?.map((item) => (
    <option key={item?.id} value={item?.id}>
      {item?.batchName}
    </option>
  ));

  const validationSchema = Yup.object().shape({
    programName: validator.programName,
    programType: validator.programType,
    modeOfStudy: validator.modeOfStudy,
    acceptanceFee: validator.amount,
    internationalAcceptanceFee: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Amount must be a number')
      .notRequired(),
    indigeneAcceptanceFee: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .typeError('Amount must be a number')
      .notRequired(),
    applicationBatch: validator.applicationBatch,
  });

  const hasRecord = Object.keys(record)?.length > 0;

  return (
    <Formik
      initialValues={{
        programName: record?.programId ?? '',
        programType: record?.programTypeId ?? '',
        modeOfStudy: record?.modeOfStudyId ?? '',
        applicationBatch: record?.applicationBatchId ?? '',
        acceptanceFee: record?.acceptanceFee ?? 0,
        internationalAcceptanceFee: record?.internationalAcceptanceFee ?? '',
        indigeneAcceptanceFee: record?.indigeneAcceptanceFee ?? '',
        status: String(record?.activeStatus ?? true),
      }}
      onSubmit={(values, { resetForm }) => {
        createUpdateApplicationFeeHandler(values, resetForm);
      }}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {() => {
        return (
          <Form>
            <section className="fields">
              <Select name="programName" placeholder="Select Program" label="Program Name" options={programOptions} />

              <Select
                name="programType"
                placeholder="Select Program Type"
                label="Program Type"
                options={programTypesOptions}
              />

              <Select
                name="modeOfStudy"
                label="Mode of Study"
                placeholder="Select Mode of Study"
                options={modeOfStudyOptions}
                loading={modeOfStudyQuery.isLoading}
              />

              <Select
                label="Application Batch"
                placeholder="Select Batch"
                name="applicationBatch"
                options={applicationBatchOptions}
              />

              {/* <Input name="applicationStartDate" type="date" placeholder="Select Date" label="Application Start Date" />
              <Input name="applicationEndDate" type="date" placeholder="Select Date" label="Application End Date" /> */}

              <Input name="acceptanceFee" placeholder="0.00" label="Amount" />

              <Input
                name="internationalAcceptanceFee"
                placeholder="0.00"
                label="International Student Acceptance Fee (Optional)"
              />

              <Input
                name="indigeneAcceptanceFee"
                placeholder="0.00"
                label="Indigene Student Acceptance Fee (Optional)"
              />

              <Select
                name="status"
                placeholder="Select Status"
                label="Status"
                options={
                  <>
                    {StatusOptions.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </>
                }
              />

              <div className="btn-group">
                <Button type="button" variant="text" text="Cancel" onClick={handleClose} />

                <Button
                  type="submit"
                  disabled={createUpdateApplicationFeeMutation.isPending}
                  isLoading={createUpdateApplicationFeeMutation.isPending}
                  text={hasRecord ? 'Update' : 'Create'}
                />
              </div>
            </section>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddAcceptanceFee;
